import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, LayoutGrid, Columns3, CalendarDays } from 'lucide-react';
import { chPostService } from '../services/postService';
import { useChAuth } from '../contexts/ChAuthContext';
import { useModal } from '../../contexts/ModalContext';
import { PostFilters, EMPTY_POST_FILTERS, type PostFilterState } from './PostFilters';
import { PostGrid } from './PostGrid';
import { PostKanban } from './PostKanban';
import { PostCalendar } from './PostCalendar';
import { PostSlideOver } from './PostSlideOver';
import { PostDetail } from './PostDetail';
import { MetricsSummaryStrip } from './MetricsDisplay';
import { FollowersEditor } from './FollowersEditor';
import { summarizeMetrics, getBestPublishingTime } from '../lib/metrics';
import { getAssigneeIds, getMetricsItem, getPlatforms } from '../lib/posts';
import { chProfileService } from '../services';
import { isChStaffRole } from '../types';
import type { ChPost, ChProject, ChPostStatus, ChPostPlatform, ChProfile, ChFollowers } from '../types';

type ViewMode = 'grid' | 'kanban' | 'calendar';

const VIEW_STORAGE_KEY = 'ch-content-view';

interface Props {
  project: ChProject;
}

export function ContentTab({ project }: Props) {
  const { user } = useChAuth();
  const { toast } = useModal();
  const [posts, setPosts] = useState<ChPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<ViewMode>(() => {
    return (localStorage.getItem(VIEW_STORAGE_KEY) as ViewMode) || 'grid';
  });
  const [filters, setFilters] = useState<PostFilterState>(EMPTY_POST_FILTERS);
  const [team, setTeam] = useState<ChProfile[]>([]);
  const [followers, setFollowers] = useState<ChFollowers | null | undefined>(project.followers);
  const [editingFollowers, setEditingFollowers] = useState(false);
  const [versionNote, setVersionNote] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState<ChPost | null>(null);
  const [selectedPost, setSelectedPost] = useState<ChPost | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedPostId = searchParams.get('post');

  // Opened from a notification: /contenthub/projetos/:slug?post=<id>
  useEffect(() => {
    if (!linkedPostId || loading) return;
    const linked = posts.find(p => p.id === linkedPostId);
    if (linked) setSelectedPost(linked);
    searchParams.delete('post');
    setSearchParams(searchParams, { replace: true });
  }, [linkedPostId, loading, posts, searchParams, setSearchParams]);

  useEffect(() => {
    loadPosts();
    setFollowers(project.followers);
  }, [project.id]);

  useEffect(() => {
    chProfileService.getAll()
      .then(all => setTeam(all.filter(p => isChStaffRole(p.role))))
      .catch(() => setTeam([]));
  }, []);

  useEffect(() => {
    localStorage.setItem(VIEW_STORAGE_KEY, view);
  }, [view]);

  async function loadPosts() {
    try {
      setLoading(true);
      const data = await chPostService.getByProject(project.id);
      setPosts(data);
    } catch {
      toast.error('Erro ao carregar os posts');
    } finally {
      setLoading(false);
    }
  }

  const filteredPosts = posts.filter(p => {
    if (filters.search && !p.title.toLowerCase().includes(filters.search.toLowerCase())) return false;
    if (filters.platform && !getPlatforms(p).includes(filters.platform)) return false;
    if (filters.assignee && !getAssigneeIds(p).includes(filters.assignee)) return false;
    if (filters.content_type && p.content_type !== filters.content_type) return false;
    if (filters.status && p.status !== filters.status) return false;
    return true;
  });

  function handlePostClick(post: ChPost) {
    setSelectedPost(post);
  }

  function handleEditPost(post: ChPost) {
    setVersionNote('');
    setEditingPost(post);
    setShowForm(true);
    setSelectedPost(null);
  }

  function handleNewPost() {
    setVersionNote('');
    setEditingPost(null);
    setShowForm(true);
  }

  function handlePostSaved(savedPost: ChPost) {
    toast.success(posts.some(p => p.id === savedPost.id) ? 'Post atualizado' : 'Post criado');
    setPosts(prev => {
      const exists = prev.find(p => p.id === savedPost.id);
      if (exists) {
        return prev.map(p => p.id === savedPost.id ? savedPost : p);
      }
      return [savedPost, ...prev];
    });
    setShowForm(false);
    setEditingPost(null);
  }

  async function handleDeletePost(postId: string) {
    try {
      await chPostService.delete(postId);
      setPosts(prev => prev.filter(p => p.id !== postId));
      setSelectedPost(null);
      toast.success('Post eliminado');
    } catch {
      toast.error('Não foi possível eliminar o post');
    }
  }

  const handleStatusChange = useCallback(async (postId: string, newStatus: ChPostStatus) => {
    if (!user) return;
    try {
      await chPostService.changeStatus(postId, newStatus, user.id, user.name);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, status: newStatus } : p));
      if (selectedPost?.id === postId) {
        setSelectedPost(prev => prev ? { ...prev, status: newStatus } : null);
      }
    } catch {
      toast.error('Não foi possível mudar o estado do post');
    }
  }, [user, selectedPost, toast]);

  function handleNewVersion(post: ChPost, changeNote: string) {
    setVersionNote(changeNote ? `Alterações pedidas: ${changeNote}`.slice(0, 200) : '');
    setEditingPost(post);
    setShowForm(true);
    setSelectedPost(null);
  }

  function handleCreateAdaptedPost(platform: ChPostPlatform, caption: string, sourcePost: ChPost) {
    const adaptedPost = {
      ...sourcePost,
      id: '',
      platform,
      platforms: [platform],
      caption,
      metrics: null,
      metrics_by_platform: null,
      published_at: null,
      status: 'ideia' as const,
      client_decision: null,
      current_version: 1,
    };
    setVersionNote('');
    setEditingPost(adaptedPost as ChPost);
    setShowForm(true);
    setSelectedPost(null);
  }

  function handlePostUpdated(updated: ChPost) {
    setPosts(prev => prev.map(p => p.id === updated.id ? updated : p));
    setSelectedPost(prev => prev && prev.id === updated.id ? updated : prev);
  }

  const summary = summarizeMetrics(posts.map(getMetricsItem));
  const publishedCount = posts.filter(p => p.status === 'publicado').length;
  const bestTime = getBestPublishingTime(posts
    .filter(p => p.status === 'publicado')
    .map(p => ({ published_at: p.published_at, metrics: getMetricsItem(p) })));
  const canEdit = isChStaffRole(user?.role);
  const names = new Map(team.map(p => [p.id, p.name]));

  if (selectedPost) {
    return (
      <PostDetail
        post={selectedPost}
        onPostUpdated={handlePostUpdated}
        onBack={() => setSelectedPost(null)}
        onEdit={handleEditPost}
        onDelete={handleDeletePost}
        onStatusChange={handleStatusChange}
        onNewVersion={handleNewVersion}
        onCreateAdaptedPost={handleCreateAdaptedPost}
      />
    );
  }

  const viewButtons: { key: ViewMode; icon: typeof LayoutGrid; label: string }[] = [
    { key: 'grid', icon: LayoutGrid, label: 'Grelha' },
    { key: 'kanban', icon: Columns3, label: 'Kanban' },
    { key: 'calendar', icon: CalendarDays, label: 'Calendário' },
  ];

  return (
    <div className="space-y-4 ch-animate-in">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-center gap-1 bg-white rounded-full p-1">
          {viewButtons.map(v => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                view === v.key
                  ? 'bg-[var(--ch-ink)] text-white'
                  : 'text-gray-500 hover:text-[var(--ch-ink)]'
              }`}
            >
              <v.icon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{v.label}</span>
            </button>
          ))}
        </div>

        <button onClick={handleNewPost} className="ch-btn ch-btn-primary ch-btn-sm">
          <Plus className="w-3.5 h-3.5" />
          Novo Post
        </button>
      </div>

      {/* Results so far */}
      {(summary.itemsWithStats > 0 || canEdit || followers) && (
        <MetricsSummaryStrip
          summary={summary}
          publishedCount={publishedCount}
          followers={followers}
          bestTime={bestTime}
          onEditFollowers={canEdit ? () => setEditingFollowers(v => !v) : undefined}
        />
      )}
      {editingFollowers && (
        <FollowersEditor
          projectId={project.id}
          followers={followers}
          onSaved={setFollowers}
          onClose={() => setEditingFollowers(false)}
        />
      )}

      {/* Filters */}
      <PostFilters filters={filters} onChange={setFilters} people={team} currentUserId={user?.id} />

      {/* Content */}
      {loading ? (
        <div className="ch-card animate-pulse space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-gray-100 rounded-xl" />
          ))}
        </div>
      ) : (
        <>
          {view === 'grid' && (
            <PostGrid posts={filteredPosts} onPostClick={handlePostClick} names={names} />
          )}
          {view === 'kanban' && (
            <PostKanban
              posts={filteredPosts}
              onPostClick={handlePostClick}
              onStatusChange={handleStatusChange}
            />
          )}
          {view === 'calendar' && (
            <PostCalendar posts={filteredPosts} onPostClick={handlePostClick} />
          )}
        </>
      )}

      {/* Slide-over form */}
      {showForm && (
        <PostSlideOver
          projectId={project.id}
          post={editingPost}
          initialVersionNote={versionNote}
          onClose={() => { setShowForm(false); setEditingPost(null); }}
          onSaved={handlePostSaved}
        />
      )}
    </div>
  );
}
